import React, { useState, useCallback } from "react";
import { GoogleMap, MarkerF, InfoWindowF } from "@react-google-maps/api";
import { supabase } from "../../supabaseClient";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import { PrimaryButton, SecondaryButton } from "../UI/DesignStandards";
import { useAuth } from "../../context/AuthContext";
import { CenterSearch } from "./CenterSearch";
import { CenterFormFields } from "./CenterFormFields";
import { CenterMap } from "./CenterMap";
import {
  googlePlaceId,
  linkOrganizationBowlingCenter,
  placeDisplayName,
  upsertBowlingCenter,
} from "../../utility/organizationBowlingCenters";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.replace(
  /[;\s]+$/,
  ""
);

const emptyCenter = () => ({
  address: "",
  centerName: "",
  phone: "",
  email: "",
  website: "",
  latitude: null,
  longitude: null,
  googlePlaceID: null,
  formattedAddress: "",
  streetFirstLine: "",
  streetSecondLine: "",
  city: "",
  state: "",
  stateShort: "",
  description: "",
  rating: null,
  userRatingCount: 0,
  priceLevel: null,
  openingHours: [],
  currentOpeningHours: null,
});

const AddCenterForm = ({
  showForm,
  onToggleForm,
  allCenters = [],
  tournamentId,
  organizationId,
  onCenterAdded,
  isLoggedIn,
  canManage,
  hideToggle = false,
  hideHeading = false,
  embedded = false,
}) => {
  const { user } = useAuth();
  const [isCheckingCenter, setIsCheckingCenter] = useState(false);
  const [error, setError] = useState(null);
  const [newCenter, setNewCenter] = useState(emptyCenter);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [map, setMap] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const clearError = () => setError(null);

  const resetForm = () => {
    setNewCenter(emptyCenter());
    setSearchResults([]);
  };

  const linkCenter = async (bowlingCenterId) => {
    if (organizationId) {
      const result = await linkOrganizationBowlingCenter({
        organizationId,
        bowlingCenterId,
        userId: user?.id,
      });
      if (result.alreadyLinked) {
        notify.info("That house is already on this organization.");
      }
      return;
    }

    const { error: addError } = await supabase
      .from("tournamentLocations")
      .insert([{ tournamentId, bowlingCenterId }]);

    if (addError) {
      console.error("Error adding to tournament:", addError);
      throw addError;
    }
  };

  const centerFromGooglePlace = (place, placeDetails) => {
    const addressComponents = placeDetails.addressComponents || [];
    const component = (type) =>
      addressComponents.find((entry) => entry.types.includes(type));

    const streetNumber = component("street_number")?.longText;
    const route = component("route")?.longText;
    const city = component("locality")?.longText;
    const state = component("administrative_area_level_1")?.longText;
    const stateShort = component("administrative_area_level_1")?.shortText;
    const postalCode = component("postal_code")?.longText;

    return {
      centerName:
        placeDisplayName(placeDetails.displayName) ||
        placeDisplayName(place.displayName) ||
        placeDisplayName(placeDetails.primaryTypeDisplayName) ||
        "Bowling Center",
      phone:
        placeDetails.internationalPhoneNumber ||
        placeDetails.nationalPhoneNumber ||
        "",
      email: "",
      website: placeDetails.websiteUri || "",
      latitude: placeDetails.location?.latitude ?? null,
      longitude: placeDetails.location?.longitude ?? null,
      googlePlaceID: googlePlaceId(place.id || place.name),
      formattedAddress: placeDetails.formattedAddress || "",
      streetFirstLine: [streetNumber, route].filter(Boolean).join(" "),
      streetSecondLine: [city, stateShort, postalCode]
        .filter(Boolean)
        .join(" "),
      city: city || "",
      state: state || "",
      stateShort: stateShort || "",
    };
  };

  const finishAdd = async (center) => {
    resetForm();
    await Promise.resolve(onCenterAdded?.(center));
    if (!hideToggle) {
      onToggleForm();
    }
  };

  const handleAddressSearch = useCallback(async (searchText) => {
    if (!searchText) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      const searchUrl = `https://places.googleapis.com/v1/places:searchText`;
      const searchResponse = await fetch(searchUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": GOOGLE_MAPS_API_KEY,
          "X-Goog-FieldMask":
            "places.displayName,places.formattedAddress,places.location,places.id",
        },
        body: JSON.stringify({
          textQuery: searchText,
          locationBias: {
            circle: {
              center: {
                latitude: 39.8283,
                longitude: -98.5795,
              },
              radius: 50000.0,
            },
          },
        }),
      });

      const searchData = await searchResponse.json();
      setSearchResults(searchData.places || []);
    } catch (error) {
      console.error("Error searching places:", error);
      setError("Failed to search locations");
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handlePlaceSelect = async (place) => {
    try {
      setIsCheckingCenter(true);
      const placeId = googlePlaceId(place.id || place.name);
      const placesUrl = `https://places.googleapis.com/v1/places/${placeId}?fields=displayName,formattedAddress,internationalPhoneNumber,nationalPhoneNumber,websiteUri,location,addressComponents,primaryTypeDisplayName&key=${GOOGLE_MAPS_API_KEY}`;

      const placesResponse = await fetch(placesUrl);
      const placeDetails = await placesResponse.json();

      if (!placeDetails.displayName && !place.displayName) {
        throw new Error("Google did not return a place name");
      }

      const googleCenter = centerFromGooglePlace(place, placeDetails);
      setNewCenter({
        ...emptyCenter(),
        ...googleCenter,
        address: googleCenter.centerName,
      });
      setSearchResults([]);

      if (!user) {
        notify.error("You must be logged in to add a bowling center");
        return;
      }

      const saved = await upsertBowlingCenter(googleCenter, user.id);
      await linkCenter(saved.id);
      await finishAdd({
        ...saved,
        centerName: googleCenter.centerName,
      });

      if (map && googleCenter.latitude && googleCenter.longitude) {
        map.panTo({
          lat: googleCenter.latitude,
          lng: googleCenter.longitude,
        });
        map.setZoom(15);
      }
    } catch (error) {
      console.error("Error:", error);
      notify.error("Failed to process place selection");
    } finally {
      setIsCheckingCenter(false);
    }
  };

  const handleCreateCenter = async () => {
    try {
      if (!user) {
        notify.error("You must be logged in to create a bowling center");
        return;
      }

      const saved = await upsertBowlingCenter(newCenter, user.id);
      await linkCenter(saved.id);
      await finishAdd({
        ...saved,
        centerName: newCenter.centerName,
      });
    } catch (error) {
      console.error("Error creating bowling center:", error);
      notify.error("Failed to create bowling center");
    }
  };

  return (
    <div className={embedded ? "w-full space-y-3" : "mb-6"}>
      {canManage && !hideToggle && !showForm ? (
        <SecondaryButton
          onClick={() => {
            onToggleForm();
            resetForm();
          }}
          className="!px-3 !py-2 text-sm"
        >
          Add bowling center
        </SecondaryButton>
      ) : null}

      {showForm && (
        <div className={embedded ? "w-full space-y-3" : "mt-4 card-inset p-4"}>
          {canManage && !hideToggle ? (
            <SecondaryButton
              onClick={onToggleForm}
              className="!px-3 !py-2 text-sm"
            >
              Cancel
            </SecondaryButton>
          ) : null}
          {hideHeading ? null : (
            <h3 className="heading-display text-lg mb-4">Add Bowling Center</h3>
          )}

          {error && (
            <InlineAlert type="error" message={error} onClose={clearError} />
          )}

          <div className="space-y-4">
            <CenterSearch
              value={newCenter.address}
              onChange={(value) => {
                setNewCenter({ ...newCenter, address: value });
                handleAddressSearch(value);
              }}
              isSearching={isSearching}
              searchResults={searchResults}
              onPlaceSelect={handlePlaceSelect}
            />

            {isCheckingCenter ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold-ink"></div>
                <span className="ml-2 text-brand-muted">
                  Checking if center exists...
                </span>
              </div>
            ) : newCenter.latitude ? (
              <>
                <CenterFormFields center={newCenter} onChange={setNewCenter} />

                <CenterMap
                  center={newCenter}
                  onMapLoad={setMap}
                  selectedMarker={selectedMarker}
                  onMarkerClick={setSelectedMarker}
                />

                <div className="flex justify-end space-x-4 mt-8">
                  {embedded ? null : (
                    <SecondaryButton
                      onClick={onToggleForm}
                      className="!px-3 !py-2 text-sm"
                    >
                      Cancel
                    </SecondaryButton>
                  )}
                  {newCenter.latitude && newCenter.longitude && (
                    <PrimaryButton onClick={handleCreateCenter}>
                      Create Center
                    </PrimaryButton>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCenterForm;
